import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { contactApi } from '@/lib/api';
import { MessageSquare, Trash2, Eye, Mail } from 'lucide-react';
import { toast } from 'sonner';

const AdminMessages = () => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMessage, setSelectedMessage] = useState(null);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const data = await contactApi.getMessages();
      setMessages(data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleView = async (message) => {
    setSelectedMessage(message);
    if (!message.is_read) {
      try {
        await contactApi.markRead(message.id);
        loadData();
      } catch (error) {
        console.error('Error:', error);
      }
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this message?')) return;
    try {
      await contactApi.delete(id);
      toast.success('Deleted');
      loadData();
      if (selectedMessage?.id === id) setSelectedMessage(null);
    } catch (error) {
      toast.error('Failed');
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
  };

  const unreadCount = messages.filter(m => !m.is_read).length;

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-stone-800">Contact Messages</h1>
        <p className="text-stone-600">
          {messages.length} messages {unreadCount > 0 && <Badge className="ml-2 bg-amber-100 text-amber-700">{unreadCount} unread</Badge>}
        </p>
      </div>

      {loading ? (
        <Card><CardContent className="p-8 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-700 mx-auto"></div>
        </CardContent></Card>
      ) : messages.length > 0 ? (
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>From</TableHead>
                <TableHead>Subject</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {messages.map((item) => (
                <TableRow key={item.id} className={!item.is_read ? 'bg-amber-50' : ''}>
                  <TableCell>
                    <div className="font-medium">{item.first_name} {item.last_name}</div>
                    <div className="text-sm text-stone-500">{item.email}</div>
                  </TableCell>
                  <TableCell>{item.subject || '(No subject)'}</TableCell>
                  <TableCell>{formatDate(item.created_at)}</TableCell>
                  <TableCell>
                    <Badge variant={item.is_read ? "secondary" : "default"}>
                      {item.is_read ? 'Read' : 'Unread'}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="sm" onClick={() => handleView(item)}>
                      <Eye className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="sm" className="text-red-600" onClick={() => handleDelete(item.id)}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      ) : (
        <Card><CardContent className="p-8 text-center">
          <MessageSquare className="h-12 w-12 text-stone-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-stone-800 mb-2">No Messages</h3>
          <p className="text-stone-600">Contact form submissions will appear here.</p>
        </CardContent></Card>
      )}

      {/* Message Detail Dialog */}
      <Dialog open={!!selectedMessage} onOpenChange={() => setSelectedMessage(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Message Details</DialogTitle>
          </DialogHeader>
          {selectedMessage && (
            <div className="space-y-4">
              <div>
                <label className="text-sm text-stone-500">From</label>
                <p className="font-medium">{selectedMessage.first_name} {selectedMessage.last_name}</p>
                <a href={`mailto:${selectedMessage.email}`} className="text-amber-700 text-sm flex items-center gap-1">
                  <Mail className="h-3 w-3" />{selectedMessage.email}
                </a>
              </div>
              <div>
                <label className="text-sm text-stone-500">Subject</label>
                <p>{selectedMessage.subject || '(No subject)'}</p>
              </div>
              <div>
                <label className="text-sm text-stone-500">Message</label>
                <p className="whitespace-pre-wrap bg-stone-50 p-3 rounded-lg">{selectedMessage.message}</p>
              </div>
              <div>
                <label className="text-sm text-stone-500">Received</label>
                <p className="text-sm">{formatDate(selectedMessage.created_at)}</p>
              </div>
              <div className="flex justify-end gap-2">
                <a href={`mailto:${selectedMessage.email}?subject=Re: ${selectedMessage.subject || 'Your message'}`}>
                  <Button className="bg-amber-700 hover:bg-amber-800"><Mail className="h-4 w-4 mr-2" /> Reply</Button>
                </a>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminMessages;
